import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { logVisitorActivity } from "@shared/lib/visitorTracking";
import axios from "axios";
import CoverPlaceholder from "@visitor/components/CoverPlaceholder";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";
import VisitorHeader from "@visitor/components/VisitorHeader";
import ScanNextButton from "@visitor/components/ScanNextButton";
import useRequireLanguage from "@visitor/components/useRequireLanguage";
import { linkTitle } from "@visitor/components/visitorNav";
import FontSizeControl from "@visitor/components/FontSizeControl";
import NarrationControl from "@visitor/features/exhibit-detail/NarrationControl";
import MediaGallery from "@visitor/features/exhibit-detail/MediaGallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZoneDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, fontSize, t } = useLanguage();
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Track which navigation key we've logged for to prevent duplicates
  const lastLoggedKey = useRef(null);

  const languageReady = useRequireLanguage();

  // Fetch zone data. `isCancelled`: the page was left (or StrictMode re-ran
  // the effect) before the response — record nothing.
  const fetchZone = async (isCancelled) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/zones/${id}`);
      if (isCancelled()) return;
      setZone(response.data);

      // Track this page visit only once per navigation
      const currentKey = `${id}-${location.key}`;
      if (lastLoggedKey.current !== currentKey) {
        lastLoggedKey.current = currentKey;
        const zoneData = response.data;
        logVisitorActivity(
          "zone",
          zoneData.id,
          zoneData.code,
          zoneData.titleTc || zoneData.titleEn,
        );
      }
    } catch (error) {
      if (isCancelled()) return;
      toast.error("Zone not found or not available");
      navigate("/qr-instructions");
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  // Fetch zone when id changes OR when navigating back/forward — only once
  // a language is chosen (a first scan detours via the language page).
  useEffect(() => {
    if (!languageReady) return undefined;
    let cancelled = false;
    fetchZone(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [id, location.key, languageReady]);

  // F-F single-playback: only one source plays at a time (narration vs a
  // gallery audio track or video). Media "play" events don't bubble but DO
  // fire capture-phase listeners on ancestors, so one document-level capture
  // listener pauses every other <audio>/<video> and cancels any browser-TTS
  // narration the moment a track starts. The TTS→media direction (starting TTS
  // while a track plays) is handled by NarrationControl's onPointerDownCapture.
  const pauseAllAudio = () => {
    document.querySelectorAll("audio, video").forEach((el) => el.pause());
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  useEffect(() => {
    const onPlayCapture = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLMediaElement)) return;
      document.querySelectorAll("audio, video").forEach((el) => {
        if (el !== target) el.pause();
      });
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
    document.addEventListener("play", onPlayCapture, true);
    return () => document.removeEventListener("play", onPlayCapture, true);
  }, []);

  const getLocalizedText = (field) => {
    if (!zone) return "";

    if (language === "tc") {
      return zone[`${field}Tc`] || zone[`${field}En`] || "";
    } else if (language === "sc") {
      return zone[`${field}Sc`] || zone[`${field}En`] || "";
    } else {
      return zone[`${field}En`] || zone[`${field}Tc`] || "";
    }
  };

  const getFontSizeClass = () => {
    if (fontSize === "small") return "text-base";
    if (fontSize === "large") return "text-2xl";
    return "text-lg";
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black"
        data-testid="loading-state"
      >
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    );
  }

  if (!zone) return null;

  const title = getLocalizedText("title");
  const description = getLocalizedText("description");
  const exhibits = zone.exhibits || [];

  return (
    <div className="min-h-screen bg-black pb-28" data-testid="zone-detail-page">
      {/* Header — parent link (the zone's exhibition) stays pinned top-left (F-AA) */}
      <VisitorHeader
        parentLabel={
          zone.exhibition ? linkTitle(zone.exhibition, language) : null
        }
        onParent={() => navigate(`/exhibition/${zone.exhibition.id}`)}
        onMenu={() => setPreferencesOpen(true)}
      />

      {/* Zone Image */}
      {zone.coverImageId && (
        <div className="relative w-full h-[40vh] bg-white/5">
          <img
            src={`${API}/assets/${zone.coverImageId}`}
            alt={title}
            className="w-full h-full object-cover"
            data-testid="zone-image"
          />
        </div>
      )}

      {/* Zone Content - Double margin */}
      <div className="px-12 pb-20 py-6 max-w-4xl mx-auto space-y-6">
        {/* Title */}
        <h2
          className="text-3xl md:text-4xl font-heading font-bold text-white"
          data-testid="zone-title"
        >
          {title}
        </h2>

        {/* Font Size Control. Narration moved to the bottom bar (F-F
            <NarrationControl>); legacy voice_url player removed. */}
        <div className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
          <div className="flex-1">
            <FontSizeControl />
          </div>
        </div>

        {/* Description */}
        <div
          className="prose prose-lg max-w-none"
          data-testid="zone-description"
        >
          <p
            className={`${getFontSizeClass()} text-justify leading-relaxed text-white/90 whitespace-pre-wrap`}
          >
            {description}
          </p>
        </div>

        {/* Media gallery (F-F) — supporting audio / video / pdf, language-filtered */}
        <MediaGallery media={zone.media} api={API} language={language} t={t} />

        {/* Exhibits Section */}
        {exhibits.length > 0 && (
          <div className="pt-6">
            <h3 className="text-2xl font-heading font-bold mb-4 text-white">
              {t({
                en: "Exhibits in this Zone",
                tc: "本展區展品",
                sc: "本展区展品",
              })}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {exhibits.map((exhibit) => (
                <button
                  key={exhibit.id}
                  onClick={() =>
                    navigate(`/exhibit/${exhibit.id}`, {
                      state: { from: location.pathname },
                    })
                  }
                  className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all text-left"
                  data-testid={`exhibit-${exhibit.id}`}
                >
                  {exhibit.coverImageId ? (
                    <div className="aspect-square bg-white/10">
                      <img
                        src={`${API}/assets/${exhibit.coverImageId}`}
                        alt={
                          exhibit[
                            `title${language[0].toUpperCase()}${language.slice(1)}`
                          ] || exhibit.titleTc
                        }
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <CoverPlaceholder
                      className="aspect-square"
                      testId={`cover-placeholder-${exhibit.id}`}
                    />
                  )}
                  <div className="p-3">
                    <p className="font-medium text-sm line-clamp-2 text-white">
                      {exhibit[
                        `title${language[0].toUpperCase()}${language.slice(1)}`
                      ] || exhibit.titleTc}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom bar — narration, survey, Scan next (F-AA) */}
      <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 p-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-x-4 gap-y-3">
          <div className="flex-1">
            <NarrationControl
              entity={zone}
              language={language}
              text={description}
              api={API}
              onTtsStart={pauseAllAudio}
            />
          </div>
          <div className="w-full md:w-auto md:min-w-56 flex gap-3">
            {zone.surveyUrl && (
              <Button
                onClick={() => window.open(zone.surveyUrl, "_blank")}
                className="h-14 flex-1 text-xl font-bold bg-red-300 text-black hover:bg-red-300/90"
                data-testid="survey-button"
              >
                {t({ en: "Survey", tc: "問卷調查", sc: "问卷调查" })}
              </Button>
            )}
            <ScanNextButton t={t} className="flex-1" />
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default ZoneDetail;
